import { css } from 'styled-components';

// Design tokens based on the reference design system
export const designTokens = {
  // Colors
  colors: {
    primary: {
      50: '#F2F9F7',
      100: '#E6F4F1',
      200: '#D1E8E2',
      300: '#A3D1C4',
      400: '#5B9B8A',
      500: '#2C5F4E',
      600: '#1D4235',
      700: '#153028',
      ring: 'rgba(44, 95, 78, 0.28)',
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    error: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
    },
    warning: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
    },
    success: {
      50: '#F0FDF4',
      100: '#DCFCE7',
      200: '#BBF7D0',
      500: '#22C55E',
      600: '#16A34A',
      700: '#15803D',
    },
    accent: {
      400: '#FFD600',
      text: '#423600',
    },
    nav: {
      activeBg: '#EFF6FF',
      activeText: '#2563EB',
    },
    whatsapp: '#25D366',
  },

  // Border radius
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 3px rgba(15,23,42,0.08)',
    md: '0 6px 18px rgba(15,23,42,0.10)',
    lg: '0 10px 25px rgba(15,23,42,0.12)',
    xl: '0 20px 40px rgba(15,23,42,0.15)',
  },

  // Typography
  fonts: {
    heading: "'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },

  // Font sizes
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  // Spacing (in rem)
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    '2xl': '2rem',   // 32px
    '3xl': '3rem',   // 48px
  },
};

// CSS custom properties for global use
export const globalStyles = css`
  :root {
    --primary-50: ${designTokens.colors.primary[50]};
    --primary-100: ${designTokens.colors.primary[100]};
    --primary-500: ${designTokens.colors.primary[500]};
    --primary-600: ${designTokens.colors.primary[600]};
    --primary-ring: ${designTokens.colors.primary.ring};

    --gray-50: ${designTokens.colors.gray[50]};
    --gray-100: ${designTokens.colors.gray[100]};
    --gray-200: ${designTokens.colors.gray[200]};
    --gray-300: ${designTokens.colors.gray[300]};
    --gray-400: ${designTokens.colors.gray[400]};
    --gray-500: ${designTokens.colors.gray[500]};
    --gray-600: ${designTokens.colors.gray[600]};
    --gray-700: ${designTokens.colors.gray[700]};
    --gray-800: ${designTokens.colors.gray[800]};
    --gray-900: ${designTokens.colors.gray[900]};

    --nav-active-bg: ${designTokens.colors.nav.activeBg};
    --nav-active-text: ${designTokens.colors.nav.activeText};

    --accent-400: ${designTokens.colors.accent[400]};
    --accent-text: ${designTokens.colors.accent.text};

    --radius-sm: ${designTokens.radius.sm};
    --radius-md: ${designTokens.radius.md};
    --radius-lg: ${designTokens.radius.lg};
    --radius-full: ${designTokens.radius.full};

    --shadow-sm: ${designTokens.shadows.sm};
    --shadow-md: ${designTokens.shadows.md};

    --brand-whatsapp: ${designTokens.colors.whatsapp};
  }
`;

export default designTokens;
